/**
Project: Voice Panel (c)
Title: Users Update Status
Description: Component to display the Users Update Status.
Copyrights: This file is subject to the terms and conditions defined in file 'LICENSE.txt', which is part of this source code package.
*/

import React, { Component } from "react";
import { connect } from "react-redux";
import {
  Flex,
  Dialog,
  Divider,
  Text,
  TableCell,
  Table,
  TableRow,
  RetryIcon,
  CloseIcon,
  Button,
} from "@fluentui/react-northstar";
import axios from "axios";
import IntlUtil from "../../../../core/helpers/intl-util";
import { UsersStatusTableHeaderColumns } from "../settings/user-status-table-header-columns";
import Texty from "react-texty";
import { VoiceAdminConstants } from "../../common/settings/voice-admin-constants";
import {
  manageError,
  checkIdentitySessionValid,
} from "../../../../core/actions/common-actions";
import {
  getTenentUsersBulk,
  setUsersAllRecords,
} from "../actions/user-actions";
import AlertInfoMessage from "../../common/helpers/alert-info-message";
import { AppConfigProps } from "../../../../core/settings/app-config";
import moment from "moment";
import { VoiceAdminURLProps } from "../../common/settings/voice-admin-urls";
import PageLoader from "../../common/helpers/page-loader";

class UsersStatus extends Component {
  _isMounted = false;
  _axiosSource = axios.CancelToken.source();
  _cancelToken = { cancelToken: this._axiosSource.token };
  _intl_ns_common = "common";
  _intl_ns_vpa_common = "vpa_common";
  _intl_ns_vpa_users = "vpa_users";

  constructor(props) {
    super(props);
    this.state = {
      usersPropagation: null,
      isPageDataFetched: false,
      overallPropagationStatus: null,
      usersBulkStatus: VoiceAdminConstants.USERS.BUlk_DATA,
    };
  }

  setStateAsync = (state) => {
    if (this._isMounted) {
      return new Promise((resolve) => {
        this.setState(state, resolve);
      });
    }
  };

  async componentDidMount() {
    this._isMounted = true;
    if (this.props.showDialogBox) {
      await this.setStateAsync({ isPageDataFetched: false });
      await this.fetchTenentUsersBulkData();
      await this.setStateAsync({ isPageDataFetched: true });
    }
  }

  async componentDidUpdate(prevProps, prevState, snapshot) {
    if (this.props.showDialogBox && !prevProps.showDialogBox) {
      await this.setStateAsync({ isPageDataFetched: false });
      await this.fetchTenentUsersBulkData();
      await this.setStateAsync({ isPageDataFetched: true });
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    this._axiosSource.cancel(
      IntlUtil.getText(
        this._intl_ns_common,
        "notification.warning.requestCancelled"
      )
    );
  }

  refreshResorcesBulk = async () => {
    await this.setStateAsync({ isPageDataFetched: false });
    const identitySession = checkIdentitySessionValid(this._cancelToken);
    if (identitySession === true) {
      await this.fetchTenentUsersBulkData();
    } else {
      this.props.history.push(VoiceAdminURLProps.home);
    }
    await this.setStateAsync({ isPageDataFetched: true });
  };

  fetchTenentUsersBulkData = async () => {
    await getTenentUsersBulk(this.state.usersBulkStatus, this._cancelToken)
      .then(async (res) => {
        if (
          res &&
          res.status === AppConfigProps.httpStatusCode.ok &&
          res.data.result
        ) {
          const propagation = res.data.result;
          await this.setStateAsync({
            usersPropagation: propagation,
          });
          if (
            this.state?.usersPropagation?.overallStatus?.toLowerCase() ===
              VoiceAdminConstants.USN_DATA_PROPAGATION.OVERALL_STATUS_COMPLETED.toLowerCase() &&
            this.props.isAlterDataPropagated === false
          )
            await this.props.fetchSubscriberNumbers();
          await this.props.refreshUserPropagationBulk(propagation);
        }
        await this.setStateAsync({ isPageDataFetched: true });
      })
      .catch(async (err) => {
        await this.setStateAsync({ isPageDataFetched: true });
        await manageError(err, this.props.history);
      });
  };

  propagationStatus = (data) => {
    let processStatus = "";
    if (
      data.processStatus ===
      VoiceAdminConstants.USN_DATA_PROPAGATION.ITEM_PROCESS_STATUS_COMPLETED
    ) {
      processStatus = data.processStatus
        ? `${data.processStatus} (${data.updateStatus})`
        : null;
    } else if (
      data.processStatus ===
        VoiceAdminConstants.USN_DATA_PROPAGATION
          .ITEM_PROCESS_STATUS_NOTSTARTED ||
      data.processStatus ===
        VoiceAdminConstants.USN_DATA_PROPAGATION.ITEM_PROCESS_STATUS_INPROGRESS
    ) {
      processStatus = "In Progress";
    }

    return processStatus;
  };

  getUsersPropagationInfoText = (propagation) => {
    if (propagation) {
      let propagationTimestamp = propagation.createTimestamp;
      if (
        propagation.endTimestamp &&
        propagation.endTimestamp !== "0001-01-01T00:00:00"
      ) {
        propagationTimestamp = propagation.endTimestamp;
      }
      const timestampObj = moment(propagationTimestamp);
      const dateString = timestampObj.format("ll");
      const timeString = timestampObj.format("LTS");

      let text = "";
      if (
        propagation.overallStatus ===
          VoiceAdminConstants.USN_DATA_PROPAGATION.OVERALL_STATUS_INPROGRESS ||
        propagation.overallStatus ===
          VoiceAdminConstants.USN_DATA_PROPAGATION.OVERALL_STATUS_NOTSTARTED
      ) {
        text = IntlUtil.getText(
          this._intl_ns_vpa_users,
          "notification.info.started"
        );
      } else if (
        propagation.overallStatus ===
        VoiceAdminConstants.USN_DATA_PROPAGATION.OVERALL_STATUS_COMPLETED
      ) {
        text = IntlUtil.getText(
          this._intl_ns_vpa_users,
          "notification.info.completed"
        );
      }
      if (timestampObj && dateString && timeString) {
        text =
          text +
          " " +
          IntlUtil.getSubstituteText(
            this._intl_ns_vpa_users,
            "notification.info.usersDataPropagationDateTime",
            [
              {
                key: "<DATE>",
                value: dateString,
              },
              {
                key: "<TIME>",
                value: timeString,
              },
            ]
          );
      }
      return text;
    } else return "";
  };

  render() {
    return (
      <>
        <Dialog
          className="vpa-usn-status-dialog"
          content={
            <>
              <Flex space="between" gap="gap.large" vAlign="center">
                <Text className="page-text text-semibold vpa-dialog-header text-fc-primary">
                  {IntlUtil.getText(
                    this._intl_ns_vpa_users,
                    "content.usersStatusTitle"
                  )}
                </Text>
                <Flex gap="gap.small">
                  {this.state.usersPropagation &&
                  this.state.usersPropagation.overallStatus &&
                  this.state.usersPropagation.overallStatus.toLowerCase() !==
                    VoiceAdminConstants.USN_DATA_PROPAGATION.OVERALL_STATUS_COMPLETED.toLowerCase() ? (
                    <Button
                      onClick={() => this.refreshResorcesBulk()}
                      icon={<RetryIcon />}
                      iconOnly
                      title="Refresh"
                    />
                  ) : null}
                  <Button
                    onClick={async () => {
                      this.props.hideDialogBox();
                    }}
                    icon={<CloseIcon />}
                    iconOnly
                    title="Close"
                  />
                </Flex>
              </Flex>
              <Divider size={1} />
              <PageLoader
                status={this.state.isPageDataFetched}
                size="medium"
                labelPosition="below"
                label={IntlUtil.getText(
                  this._intl_ns_common,
                  "content.loadingInprogress"
                )}
                type="center"
              >
                <Flex className="m-t-20">
                  <AlertInfoMessage
                    fitted={true}
                    content={this.getUsersPropagationInfoText(
                      this.state.usersPropagation
                    )}
                  />
                </Flex>
                {this.state.usersPropagation &&
                this.state.usersPropagation.data &&
                this.state.usersPropagation.data.length > 0 ? (
                  <Table className="vpa-usn-dialog-table-wrapper">
                    <Flex className="vpa-usn-dialog-table-header-wrapper">
                      <TableRow
                        header
                        className="vpa-usn-dialog-table-header-row"
                      >
                        {UsersStatusTableHeaderColumns(
                          this._intl_ns_vpa_users
                        ).map((column, index) => {
                          return (
                            <TableCell
                              key={`user-status-header-${index}`}
                              className={column.cellClassName}
                            >
                              {column.content}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    </Flex>
                    <Flex column className="vpa-usn-dialog-table-data-wrapper">
                      {this.state.usersPropagation.data.map((data, index) => {
                        return (
                          <TableRow
                            key={`user-status-list-${index}`}
                            className="vpa-usn-dialog-table-data-row"
                          >
                            <TableCell className="vpa-usn-dialog-table-data-cell">
                              <Texty className="page-text">{data.email}</Texty>
                            </TableCell>
                            <TableCell className="vpa-usn-dialog-table-data-cell max-w-120">
                              <Texty className="page-text">
                                {data.paramName}
                              </Texty>
                            </TableCell>
                            <TableCell className="vpa-usn-dialog-table-data-cell max-w-170">
                              <Texty className="page-text">
                                {data.paramValue}
                                {data.actionType
                                  ? ` (${data.actionType})`
                                  : null}
                              </Texty>
                            </TableCell>
                            <TableCell className="vpa-usn-dialog-table-data-cell max-w-170">
                              <Texty className="page-text">
                                {this.propagationStatus(data)}
                              </Texty>
                            </TableCell>
                            <TableCell className="vpa-usn-dialog-table-data-cell">
                              <Texty className="page-text">
                                {data.errorMessage}
                              </Texty>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </Flex>
                  </Table>
                ) : (
                  <Flex inline gap="gap.large" className="m-t-10">
                    <Flex>
                      <Text className="page-text text-fs-s text-fc-dark">
                        {IntlUtil.getText(
                          this._intl_ns_vpa_users,
                          "notification.info.noUsersResultFound"
                        )}
                      </Text>
                    </Flex>
                  </Flex>
                )}
              </PageLoader>
            </>
          }
          open={this.props.showDialogBox}
        />
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  usersAllRecords: state.userStore.usersAllRecords,
});

const mapActionToProps = {
  setUsersAllRecords,
};

export default connect(mapStateToProps, mapActionToProps)(UsersStatus);
